import * as React from "react";
import { LogIn } from "lucide-react";

import useEnsureUserProfile from "@/hooks/use-ensure-user-profile";
import { parseRoles } from "@/lib/permission";
import { cn } from "@/lib/utils";
import { PersonOutline } from "@mui/icons-material";

/**
 * My Profile is a page of the AeroTrak app (AeroStaff stays the source of the
 * data), so every app links out to it rather than rendering its own copy.
 * Read from VITE_AEROTRAK_BASE_URL, not appConfig.appUrls.aerotrak: that one
 * falls back to "/" while the config is loading, which would send the user to
 * this app's own "/me".
 */
function getMyProfileUrl(): string {
  const aerotrakOrigin = (import.meta.env.VITE_AEROTRAK_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  return `${aerotrakOrigin}/me`;
}

const menuItemClassName = cn(
  "aero:flex aero:w-full aero:items-center aero:gap-3 aero:px-4 aero:py-3 aero:cursor-pointer",
  "aero:text-sm aero:leading-5 aero:text-neutral-900 aero:transition-colors",
  "aero:hover:bg-neutral-100 aero:hover:font-bold aero:active:bg-neutral-200",
  "aero:focus-visible:outline-none aero:focus-visible:bg-neutral-100",
);

interface UserMenuProps {
  onLogout: () => void;
  /** Called before navigating/logging out, e.g. to close the parent popover. */
  onItemSelect?: () => void;
}

/**
 * Content of the avatar popover (header and mobile footer): who is signed in,
 * a link to their AeroTrak My Profile page, and Log Out. Reads the profile from
 * the shared user-profile store, so callers only pass the logout handler.
 */
export function UserMenu({ onLogout, onItemSelect }: UserMenuProps) {
  const { userProfile, isLoading } = useEnsureUserProfile();

  const userName =
    (isLoading && "Loading...") ||
    userProfile?.fullName ||
    userProfile?.userName ||
    "User";
  const userRole = React.useMemo(() => {
    const roles = parseRoles(userProfile?.roles);
    return roles.length > 0 ? roles.join(", ") : "User";
  }, [userProfile?.roles]);

  return (
    <div className="aero:flex aero:flex-col" role="menu">
      <div className="aero:flex aero:flex-col aero:gap-1 aero:px-4 aero:py-3">
        <span className="aero:text-sm aero:font-bold aero:leading-5 aero:uppercase aero:text-neutral-900 aero:break-words">
          {userName}
        </span>
        <span className="aero:text-[10px] aero:leading-3 aero:uppercase aero:tracking-[0.4px] aero:text-neutral-500 aero:break-words">
          {userRole}
        </span>
      </div>

      <div className="aero:h-px aero:bg-neutral-300" />

      <a
        href={getMyProfileUrl()}
        role="menuitem"
        className={menuItemClassName}
        onClick={onItemSelect}
      >
        <PersonOutline className="aero:h-4 aero:w-4 aero:shrink-0" />
        <span>My Profile</span>
      </a>

      <div className="aero:h-px aero:bg-neutral-300" />

      <button
        type="button"
        role="menuitem"
        className={menuItemClassName}
        onClick={() => {
          onItemSelect?.();
          onLogout();
        }}
      >
        <LogIn className="aero:h-4 aero:w-4 aero:shrink-0" />
        <span>Log Out</span>
      </button>
    </div>
  );
}
